import {tagApi} from "@core/api";
import {
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import {translate} from "@util";
import {useEffect, useState} from "react";
import {Select} from "./Select";
import {CancelIcon} from "./icons";

const TagsSelector = ({
  sx,
  component = "menu",
  label,
  value: valueProp,
  variant,
  error,
  onChange,
}: {
  sx?: SxProps<Theme>;
  component?: "menu" | "chips";
  label?: string;
  value?: string[];
  variant?: "text" | "outlined" | "contained";
  error?: string;
  onChange: (tags: string[]) => void;
}) => {
  const theme = useTheme();
  const {data: tags, isLoading} = tagApi.useFindAllEntity(undefined);

  const [value, setValue] = useState<{value: string; label: string}[]>([]);
  const [options, setOptions] = useState<{value: string; label: string}[]>();

  useEffect(() => {
    const options = tags
      ?.filter(t => !value.some(v => v.value === t.key))
      .map(t => ({label: t.label, value: t.key}));

    setOptions(options);
  }, [value]);

  useEffect(() => {
    if (tags && valueProp) {
      const v = valueProp
        .map(v => {
          const t = tags.find(t => t.key === v);
          return t ? {value: t.key, label: t.label} : undefined;
        })
        .filter((v): v is {value: string; label: string} => !!v);
      setValue(v);
    }
  }, [valueProp, tags]);

  const handleChange = (newValue: string) => {
    if (newValue) {
      const newTags = [...value, {value: newValue, label: newValue}].map(
        v => v.value,
      );
      onChange(newTags);
    }
  };

  const handleDelete = (valueToDelete: string) => {
    const newTags = value
      .filter(v => v.value !== valueToDelete)
      .map(v => v.value);
    onChange(newTags);
  };

  const handleToggle = (tagKey: string) => {
    const isSelected = value.some(v => v.value === tagKey);
    let newTags: string[];
    if (isSelected) {
      newTags = value.filter(v => v.value !== tagKey).map(v => v.value);
    } else {
      newTags = [...value.map(v => v.value), tagKey];
    }
    onChange(newTags);
  };

  if (isLoading) return <CircularProgress size={24} />;

  if (tags?.length === 0) return null;

  if (component === "menu") {
    return (
      <Stack sx={sx}>
        {label && (
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {translate(label)}
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error" gutterBottom>
            {translate(error)}
          </Typography>
        )}
        <Stack direction={"row"} spacing={1} alignItems={"center"}>
          {options && options.length > 0 && (
            <Select<string>
              placeholder={translate("common.action.select")}
              options={options}
              onChange={handleChange}
              variant={variant}
            />
          )}
          {value?.map(v => {
            const t = tags?.find(t => t.key === v.value);
            const textColor = theme.palette.getContrastText(
              t ? t.color : "white",
            );
            return (
              <Chip
                key={v.value}
                label={t ? t.label : v.label}
                sx={{
                  bgcolor: t?.color,
                  color: textColor,
                  "& .MuiChip-deleteIcon": {color: textColor},
                }}
                onDelete={() => handleDelete(v.value)}
              />
            );
          })}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack sx={sx} direction={"row"} spacing={1} alignItems={"start"}>
      <Stack
        sx={sx}
        direction={"row"}
        spacing={1}
        pt={"3px"}
        height={40}
        alignItems={"start"}
        overflow={"auto"}>
        {tags?.map(t => {
          const selected = value.some(v => v.value === t.key);
          const textColor = theme.palette.getContrastText(
            t ? t.color : "white",
          );
          return (
            <Chip
              key={t.key}
              label={t.label}
              variant={selected ? "filled" : "outlined"}
              size="small"
              onClick={() => handleToggle(t.key)}
              sx={{
                "&:hover": {
                  bgcolor: selected ? t?.color : undefined,
                },
                bgcolor: selected ? t?.color : undefined,
                color: selected ? textColor : undefined,
              }}
            />
          );
        })}
      </Stack>
      <IconButton
        size="small"
        onClick={() => onChange([])}
        aria-label="delete all tags">
        <CancelIcon fontSize="inherit" />
      </IconButton>
    </Stack>
  );
};

export {TagsSelector};
